import { Component, Input, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { RosterRef } from '../../../invitations/models/invitation.model';
import { FsComboOption } from '../../../common/fs-combobox/fs-combobox.component';
import { ReportType } from '../../models/report.model';

/** One selectable report-type card. `keyText`/`purchasable` are placeholder — pricing and the
 *  purchase flow are a pending product decision, so nothing here debits or charges. */
interface TypeCard {
  type: ReportType;
  title: string;
  desc: string;
  /** What the type costs to run, in words. */
  keyText: string;
  /** No key required — rendered as a green "Free" affordance instead of a purchase line. */
  free: boolean;
}

@Component({
  selector: 'app-report-step-details',
  templateUrl: './step-report-details.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class ReportStepDetailsComponent {
  @Input() form!: FormGroup;
  @Input() orgs: RosterRef[] = [];

  /**
   * The one type that can be bought right now. The other paid type shows "purchase coming soon"
   * until the pricing/purchase decision lands. Dummy for now — flip this (or add a set) when the
   * decision is made; no other code depends on the choice.
   */
  readonly purchasableType: ReportType = 'group_profile_plus';

  readonly typeCards: TypeCard[] = [
    {
      type: 'group_profile', title: 'Group Profile',
      desc: 'The combined high and low preferences of each group member.',
      keyText: 'No key required', free: true,
    },
    {
      type: 'group_profile_plus', title: 'Group Profile Plus',
      desc: 'Group profile plus names and high/neutral/low preferences of each group member.',
      keyText: 'Uses 1 Group Profile Plus key', free: false,
    },
    {
      type: 'team_member_report', title: 'Team Member Profile',
      desc: 'Individual profile overlaid with the group profile along with team and collaboration advice.',
      keyText: 'Uses 1 Team Report key', free: false,
    },
  ];

  get orgOptions(): FsComboOption[] { return this.orgs.map(o => ({ id: o.id, name: o.name })); }

  showError(name: string): boolean {
    const c = this.form.get(name);
    return !!c && c.invalid && c.touched;
  }

  onOwnerChange(id: string): void {
    this.form.get('ownerOrgId')!.setValue(id);
    this.form.get('ownerOrgId')!.markAsTouched();
  }

  selectType(t: ReportType): void { this.form.get('type')!.setValue(t); }
  isSelectedType(t: ReportType): boolean { return this.form.value.type === t; }
  isPurchasable(t: ReportType): boolean { return t === this.purchasableType; }

  /** Placeholder buy action — the real purchase flow is a pending decision. */
  buyKeys(t: ReportType, ev: Event): void {
    ev.stopPropagation();
    Swal.fire({
      icon: 'info',
      title: 'Coming soon',
      text: 'Buying keys from here is still being finalized.',
      confirmButtonColor: '#69be28',
    });
  }
}
